import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Divider, Steps } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  ShopOutlined,
  CheckCircleOutlined,
  RocketOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import '../AuthPage.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

/**
 * Client onboarding authentication page
 */
const ClientAuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next') || '/admin';
      history.replace(nextUrl);
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      clearError();
      setTimeout(() => {
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with client role context
  const handleOIDCLogin = () => {
    clearError();
    login("client");
  };

  // Handle OIDC registration with client role context
  const handleOIDCRegister = () => {
    clearError();
    register("client");
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main client onboarding page
  return (
    <div className="auth-container">
      <Card className="auth-card onboarding-card">
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <ShopOutlined className="auth-icon client-icon" />
            <Title level={1} className="auth-title">
              Welcome to StrideAhead
            </Title>
            <Text className="auth-subtitle">
              Join thousands of organizations transforming their educational delivery
            </Text>
          </div>

          {/* Onboarding Steps */}
          <div className="onboarding-steps">
            <Steps size="small" current={0}>
              <Step 
                title="Sign Up" 
                icon={<UserOutlined />} 
                description="Create your account"
              />
              <Step 
                title="Setup" 
                icon={<ShopOutlined />} 
                description="Configure your organization"
              />
              <Step 
                title="Launch" 
                icon={<RocketOutlined />} 
                description="Start using the platform"
              />
            </Steps>
          </div>

          {/* Benefits */}
          <div className="onboarding-benefits">
            <Title level={4}>What you&apos;ll get:</Title>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Complete learning management system</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Advanced analytics and reporting</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Student engagement tools</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>24/7 support and training</Text>
              </div>
            </div>
          </div>

          {/* Auth Actions */}
          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCRegister}
              className="auth-button oidc-button primary-cta"
              icon={<UserOutlined />}
              loading={isLoading}
            >
              Start Your Free Trial
            </Button>
            
            <Divider>Already have an account?</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button login-button"
              icon={<LockOutlined />}
            >
              Sign In
            </Button>
          </div>

          {/* Security Info */}
          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Enterprise-grade security with Stride ID
            </Paragraph>
            <Paragraph className="auth-info small">
              Your data is protected with industry-standard encryption and security protocols
            </Paragraph>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <Text type="secondary">
              Need help getting started? <a href="mailto:support@strideahead.com">Contact our team</a>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ClientAuthPage;